/**
 * OIDC post-login landing.
 *
 * Backend redirects here (`/auth/callback`) after a successful
 * JumpCloud SSO flow. By the time we land here the access + refresh
 * JWT cookies are already set on the browser. All we have to do is
 * refresh the AuthContext so React picks up the new identity, then
 * forward the user to wherever they were going (sessionStorage
 * `oidc_post_login_next`, falling back to "/").
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";

const safeNext = (raw) => {
  if (!raw || typeof raw !== "string") return "/";
  if (!raw.startsWith("/")) return "/";
  if (raw.startsWith("//")) return "/";
  if (raw.includes(":")) return "/";
  return raw;
};

export default function AuthCallback() {
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refresh();
      } catch (_) {
        // refresh swallows errors and sets user=null — ProtectedRoute
        // will bounce us back to /login if so.
      }
      if (cancelled) return;
      let next = "/";
      try {
        next = safeNext(sessionStorage.getItem("oidc_post_login_next") || "/");
        sessionStorage.removeItem("oidc_post_login_next");
      } catch (_) { /* private mode */ }
      navigate(next, { replace: true });
    })();
    return () => { cancelled = true; };
  }, [refresh, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex items-center gap-3 text-slate-600 text-sm" data-testid="auth-callback-spinner">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Finishing sign-in…</span>
      </div>
    </div>
  );
}
